import PropTypes from 'prop-types';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { AutoScroller } from '@dnd-kit/dom';
import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import { Icon } from '@gouvfr-lasuite/ui-kit';

import CardContainer from '../../../containers/CardContainer';
import SubtaskGroup from '../List/SubtaskGroup';
import LeafListContainer from '../../../containers/LeafListContainer'; // ─── C2 ───
import { useCardDndHandlers } from '../../../hooks'; // ─── C2 ───

import globalStyles from '../../../assets/styles/styles.module.scss'; // ⚠️ vérifier le nombre de "../"

import styles from './HierarchyView.module.scss';

const HierarchyView = React.memo(
  ({ lists, layout, parentNames, boardId, onCardMove, onCardTransfer }) => {
    const [collapsedLevels, setCollapsedLevels] = useState({});

    const handleToggleLevel = useCallback((level) => {
      setCollapsedLevels((prev) => ({ ...prev, [level]: !prev[level] }));
    }, []);

    // Couleur de fond de liste (réutilise la convention du ColorPicker / ListContainer)
    const listColorClass = (color) =>
      color ? globalStyles[`background${upperFirst(camelCase(color))}`] : null;

    // ─── C2 : DnD du couloir feuilles (SANS onListMove : pas de tri de listes) ───
    const { handleDragStart, handleDragEnd } = useCardDndHandlers({
      boardId,
      onCardTransfer,
      onCardMove,
    });

    const providerPlugins = useCallback(
      (defaults) => [...defaults, AutoScroller.configure({ layoutShiftCompensation: false })],
      [],
    );

    if (!layout || !lists || lists.length === 0) {
      return null;
    }

    const { lanes, maxLevel, groupsByLevel } = layout;

    const levels = [];
    for (let lvl = maxLevel; lvl >= 0; lvl -= 1) {
      levels.push(lvl);
    }

    const levelLabel = (level) => {
      if (level === 0) return 'Feuilles';
      if (level === maxLevel) return `Niveau ${level} (racines)`;
      return `Niveau ${level}`;
    };

    // Rendu read-only d'une cellule (niveaux ≥ 1)
    const renderReadOnlyCell = (level, list) => {
      const cell = (groupsByLevel[level] && groupsByLevel[level][list.id]) || {
        parentGroups: {},
        orphanCardIds: [],
      };
      const orderedCardIds = (lanes[level] && lanes[level][list.id]) || [];

      const orphanSet = new Set(cell.orphanCardIds.map(String));
      const parentIdByCardId = new Map();
      Object.keys(cell.parentGroups).forEach((parentId) => {
        cell.parentGroups[parentId].forEach((childId) => {
          parentIdByCardId.set(String(childId), String(parentId));
        });
      });
      const renderedGroups = new Set();

      return orderedCardIds.map((cardId) => {
        const key = String(cardId);
        if (orphanSet.has(key)) {
          return (
            <div key={cardId} className={styles.card}>
              <CardContainer id={cardId} />
            </div>
          );
        }
        const parentId = parentIdByCardId.get(key);
        if (parentId !== undefined) {
          if (renderedGroups.has(parentId)) return null;
          renderedGroups.add(parentId);
          return (
            <SubtaskGroup
              key={`group-${level}-${list.id}-${parentId}`}
              parentId={parentId}
              parentName={parentNames[parentId]}
              childIds={cell.parentGroups[parentId]}
              readOnly
            />
          );
        }
        return (
          <div key={cardId} className={styles.card}>
            <CardContainer id={cardId} />
          </div>
        );
      });
    };

    // Compteur de cartes d'une cellule (Q1)
    const cellCount = (level, listId) => ((lanes[level] && lanes[level][listId]) || []).length;

    console.log('BG class:', globalStyles[`background${upperFirst(camelCase('lagoon-blue'))}`]);
    console.log('COLOR class:', globalStyles[`color${upperFirst(camelCase('lagoon-blue'))}`]);
    console.log(
      'globalStyles keys:',
      Object.keys(globalStyles).filter((k) => k.toLowerCase().includes('lagoon')),
    );

    return (
      <DragDropProvider
        plugins={providerPlugins}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className={styles.grid}>
          {/* En-têtes colonnes */}
          <div className={styles.headerRow}>
            {lists.map((list) => (
              <div
                key={list.id}
                className={classNames(
                  styles.headerCell,
                  list.color ? styles.headerCellColored : null, // ← marqueur explicite
                  listColorClass(list.color),
                )}
                title={list.name}
              >
                {list.name}
              </div>
            ))}
          </div>

          {/* Lignes de niveaux */}
          {levels.map((level) => {
            const isCollapsed = !!collapsedLevels[level];
            const isLeaf = level === 0;

            return (
              <div key={level} className={styles.lane}>
                {/* ─── Bandeau de niveau pleine largeur (façon Jira swimlane) ─── */}
                <button
                  type="button"
                  className={classNames(styles.laneHeader, isLeaf && styles.laneHeaderLeaf)}
                  onClick={() => handleToggleLevel(level)}
                >
                  <Icon name={isCollapsed ? 'chevron_right' : 'expand_more'} />
                  <span className={styles.laneHeaderText}>{levelLabel(level)}</span>
                </button>

                {/* ─── Rangée de colonnes flottantes ─── */}
                {!isCollapsed && (
                  <div className={styles.laneColumns}>
                    {lists.map((list) => {
                      const count = cellCount(level, list.id);
                      // Niveaux ≥ 1 : colonne vide → placeholder transparent (garde l'alignement)
                      const isHiddenEmpty = !isLeaf && count === 0;

                      return (
                        <div
                          key={list.id}
                          className={classNames(
                            styles.column,
                            isLeaf && styles.leafColumn,
                            isHiddenEmpty && styles.columnHidden, // ← masque le visuel, garde la place
                          )}
                        >
                          {!isHiddenEmpty && (
                            <>
                              <div className={styles.columnHeader}>
                                <span className={styles.columnCount}>{count}</span>
                              </div>
                              <div className={styles.columnContent}>
                                {isLeaf ? (
                                  <LeafListContainer id={list.id} />
                                ) : (
                                  renderReadOnlyCell(level, list)
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* DragOverlay pour les feuilles */}
        <DragOverlay>
          {(source) =>
            source.type === 'Card' ? (
              <div className={styles.card}>
                <CardContainer id={source.id} />
              </div>
            ) : null
          }
        </DragOverlay>
      </DragDropProvider>
    );
  },
);

HierarchyView.propTypes = {
  lists: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  layout: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  parentNames: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  boardId: PropTypes.string.isRequired, // ─── C2 ───
  onCardMove: PropTypes.func.isRequired, // ─── C2 ───
  onCardTransfer: PropTypes.func.isRequired, // ─── C2 ───
};

HierarchyView.defaultProps = {
  layout: null,
  parentNames: {},
};

export default HierarchyView;
